import React, { useMemo } from 'react';
import { Typography } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { truncate } from 'lodash';
import { hooks, selectors } from 'farmx-redux-core';
import ConnectivityStateTag from '../../components/ConnectivityStateTag';
import { IconTitle } from '../IconTitle';
import { ValueUnitInfo } from '../ValueUnitInfo';
import { getConnectivityLabel } from '../../../../helper/listHelper';
import { CardLayout } from '../../components/CardLayout';
import { NoSummaryData } from './NoSummaryData';
import { LABEL_CELSIUS, LABEL_FAHRENHEIT } from '../../../../constants';

const {
  useLoadStatus, useUnits,
} = hooks;

const {
  selectTHSensorSummary,
  selectSensorLoadingStatus,
  selectUserTemperatureFormat,
} = selectors;

const defaultTemperatureUnit = 'degrees_celsius';

interface TemperatureHumiditySummaryProps {
  list: any[]
}

export const TemperatureHumiditySummary: React.FC<TemperatureHumiditySummaryProps> = (
  props: any
) => {
  const { list } = props;
  const { t } = useTranslation();
  const getUserUnits = useUnits();

  const temperatureFormat = useSelector(selectUserTemperatureFormat);
  const TEMPERATURE_LABEL = temperatureFormat === defaultTemperatureUnit
    ? LABEL_CELSIUS : LABEL_FAHRENHEIT;
  const summary = useSelector((store) => selectTHSensorSummary(store, list)) || {};
  const loading = useSelector((store) => selectSensorLoadingStatus(store, list));

  const {
    onlineCount, names, hMax, hMin, tMax, tMin
  } = summary;

  const {value: temperatureMin} = getUserUnits(tMin, defaultTemperatureUnit, 'temperature', {decimalPlaces: 3});
  const {value: temperatureMax} = getUserUnits(tMax, defaultTemperatureUnit, 'temperature', {decimalPlaces: 3});

  const stateTag = useMemo(() => (
    getConnectivityLabel(onlineCount, list.length, t)
  ), [onlineCount, list, t]);

  const showNoDataInfo = (!loading && !list.length);

  const valueUnitInfoType = useMemo(() => (
    stateTag.type === 'online' ? 'primary' : 'disabled'),
  [stateTag.type]);

  useLoadStatus(list);

  if (showNoDataInfo) {
    return (
      <NoSummaryData
        icon="icon-thermometer"
        title={t('Temperature Humidity Sensors')}
      />
    );
  }

  return (
    <CardLayout style={{ minHeight: '150px'}}>
      <div className="flex-row space-between">
        <IconTitle
          iconName="icon-thermometer"
          title={t('Temperature Humidity Sensors')}
        />
        {!loading && (
          <ConnectivityStateTag
            label={stateTag.label}
            color={stateTag.color}
          />
        )}
      </div>
      {loading ? (
        <LoadingOutlined />
      ) : (
        <>
          <div className="flex-row space-between margin-top-20">
            <div className="flex-row">
              <ValueUnitInfo
                type={valueUnitInfoType}
                value={temperatureMin}
                unit={TEMPERATURE_LABEL}
                info={t('min')}
                valueClassName="small-value-text"
                unitClassName="small-unit-text"
              />

              <Typography.Text strong className="separator primary-color">{' / '}</Typography.Text>

              <ValueUnitInfo
                type={valueUnitInfoType}
                value={temperatureMax}
                unit={TEMPERATURE_LABEL}
                info={t('max')}
                valueClassName="small-value-text"
                unitClassName="small-unit-text"
              />
            </div>
            <div className="flex-row">
              <ValueUnitInfo
                type={valueUnitInfoType}
                value={hMin}
                unit="%RH"
                info={t('min')}
                valueClassName="small-value-text"
                unitClassName="small-unit-text"
              />

              <Typography.Text strong className="separator primary-color">{' / '}</Typography.Text>

              <ValueUnitInfo
                type={valueUnitInfoType}
                value={hMax}
                unit="%RH"
                info={t('max')}
                valueClassName="small-value-text"
                unitClassName="small-unit-text"
              />
            </div>
          </div>
          <div className="flex-row flex-end margin-top-10">
            <Typography.Text className="card-footer-txt">{truncate(names?.join(', '))}</Typography.Text>
          </div>
        </>
      )}
    </CardLayout>
  );
};
